import { useContext, useEffect, useState, useRef } from 'react';
import { RxNowUnreadContext } from '../contexts/RxNowUnreadContext';
import { UserContext } from '../contexts/UserContext';

/**
 * Custom hook to manage Mediprocity unread message count
 * Initializes the Mediprocity widget and sets up event listeners for
 * unread count changes and new notifications
 */
const useMediprocityUnreadMessages = () => {
    const { unreadCount, setUnreadCount, hasNewMessage, setHasNewMessage } = useContext(RxNowUnreadContext);
    const { user } = useContext(UserContext);
    const initializedRef = useRef(false);

    console.log('🎣 useMediprocityUnreadMessages hook called');
    console.log('📋 user object:', user);
    console.log('🔐 user.mediprocityInfo:', user?.mediprocityInfo);

    useEffect(() => {
        console.log('⚡ useEffect triggered');
        console.log('📝 Condition checks:');
        console.log('  - user?.mediprocityInfo?.user:', user?.mediprocityInfo?.user);
        console.log('  - user?.mediprocityInfo?.password:', user?.mediprocityInfo?.password);
        console.log('  - typeof window:', typeof window);
        console.log('  - initializedRef.current:', initializedRef.current);
        
        // Only initialize if user has Mediprocity credentials and we're in browser
        if (!user?.mediprocityInfo?.user || !user?.mediprocityInfo?.password || typeof window === 'undefined') {
            console.warn('⛔ Hook skipped - missing user credentials or not in browser environment');
            return;
        }

        // Prevent multiple initializations
        if (initializedRef.current) {
            console.warn('⛔ Hook skipped - already initialized');
            return;
        }
        initializedRef.current = true;

        // Check if Mediprocity script is loaded
        if (typeof window.Mediprocity === 'undefined') {
            console.warn('⛔ Mediprocity script not loaded yet');
            return;
        }
        
        console.log('✨ Proceeding with Mediprocity initialization...');

        try {
            const options = {
                container: '#mediprocity-hidden-container', // Mount in hidden container
                email: user.mediprocityInfo.user,
                password: user.mediprocityInfo.password,
                encryption_key: user.mediprocityInfo.encryptionKey,
                url: 'https://secure.specialtyrxnow.com/',
                // Don't create UI elements - we only want event callbacks
                popup: false,
                // Callback for unread count changes
                onUnreadCountChange: (count) => {
                    try {
                        console.log('📊 Mediprocity onUnreadCountChange triggered - count:', count);
                        setUnreadCount(count);
                        setHasNewMessage(count > 0);
                        console.log('✅ Context updated - unreadCount:', count, 'hasNewMessage:', count > 0);
                    } catch (error) {
                        console.error('❌ Error updating unread count:', error);
                    }
                },
                // Callback for new notifications
                onNewNotification: (notification) => {
                    try {
                        console.log('🔔 New Mediprocity notification:', notification);
                        setHasNewMessage(true);
                        console.log('✅ hasNewMessage set to true');
                        // Trigger a recount on new notification
                        if (window.mediprocityClientInstance && window.mediprocityClientInstance.getUnreadCount) {
                            console.log('⏳ Calling getUnreadCount()...');
                            window.mediprocityClientInstance.getUnreadCount();
                        }
                    } catch (error) {
                        console.error('❌ Error handling new notification:', error);
                    }
                },
            };

            // Create the Mediprocity instance silently (no UI)
            const client = new window.Mediprocity(options);
            console.log('🚀 Mediprocity client created');

            // Store instance globally for later access if needed
            window.mediprocityClientInstance = client;

            // Add CSS to hide any potential Mediprocity overlays that might interfere
            const style = document.createElement('style');
            style.textContent = `
                .mediprocity-overlay, .mediprocity-modal, .mediprocity-popup,
                [class*="mediprocity"][class*="overlay"],
                [class*="mediprocity"][class*="modal"],
                [class*="mediprocity"][class*="popup"] {
                    display: none !important;
                    visibility: hidden !important;
                    opacity: 0 !important;
                    pointer-events: none !important;
                }
            `;
            document.head.appendChild(style);

            // Initialize the widget
            client.init();
            console.log('✅ Mediprocity widget initialized');

        } catch (error) {
            console.error('❌ Error initializing Mediprocity for unread messages:', error);
        }

        // Cleanup is handled by component unmount
        return () => {
            // Optional: Destroy client instance on unmount
            if (window && window.mediprocityClientInstance) {
                try {
                    window.mediprocityClientInstance = null;
                } catch (error) {
                    console.error('Error cleaning up Mediprocity instance:', error);
                }
            }
        };
    }, [user?.mediprocityInfo?.user]);

    return { unreadCount, hasNewMessage };
    
};

export default useMediprocityUnreadMessages;
